<template>
  <div class="form-wizard-step">
    <div class="form-wizard-step-heading ion-text-center">
      <ion-img :src="require('@/assets/images/icons/avatar.svg')" alt="user-avatar"></ion-img>
      <h1>Gazdi adatai</h1>
    </div>
    <form @submit.prevent="submit" novalidate>
      <div class="form-group">
        <ion-label for="name" class="control-label"> Neved: </ion-label>
        <ion-input
          id="name"
          v-model="validations.userName.$model"
          name="name"
          inputmode="text"
          enterkeyhint="next"
          class="form-control"
          :class="{ 'is-invalid': validations.userName.$error || hasServerValidationError('first_name') }"
          autocapitalize="sentences"
          required
        />
        <ErrorMessage :field="validations.userName" />
        <ServerValidation field="first_name" :server-validation="serverValidation" />
      </div>
      <div class="form-group">
        <ion-label for="email" class="control-label"> E-mail cím: </ion-label>
        <ion-input
          id="email"
          type="email"
          v-model="validations.userEmail.$model"
          name="email"
          inputmode="email"
          enterkeyhint="next"
          class="form-control"
          :class="{ 'is-invalid': validations.userEmail.$error || hasServerValidationError('email') }"
          required
        />
        <ErrorMessage :field="validations.userEmail" />
        <ServerValidation field="email" :server-validation="serverValidation" />
      </div>
      <div class="form-group">
        <div class="form-group--checkbox">
          <ion-checkbox
            id="terms-of-use"
            v-model="validations.termsOfUse.$model"
            name="terms-of-use"
            class="form-checkbox"
            :class="{ 'is-invalid': validations.termsOfUse.$error }"
            required
          />
          <ion-label for="terms-of-use" class="control-label">
            Elfogadom a <a href="https://dox-tag.eu/eula/" target="_blank">Felhasználási feltételeket</a>.
          </ion-label>
        </div>
        <ErrorMessage :field="validations.termsOfUse" />
      </div>
      <div class="form-group">
        <div class="form-group--checkbox">
          <ion-checkbox
            id="privacy-policy"
            v-model="validations.privacyPolicy.$model"
            name="privacy-policy"
            class="form-checkbox"
            :class="{ 'is-invalid': validations.privacyPolicy.$error }"
            required
          />
          <ion-label for="privacy-policy" class="control-label">
            Elfogadom az <a href="https://dox-tag.eu/privacy-policy/" target="_blank">Adatkezelési elveket</a>.
          </ion-label>
        </div>
        <ErrorMessage :field="validations.privacyPolicy" />
      </div>

      <div class="form-wizard-actions">
        <ion-button
          type="submit"
          id="submit"
          expand="full"
          shape="round"
          :disabled="isLoading"
          strong="true"
          class="btn"
        >
          {{ isLoading ? 'Loading' : 'Tovább' }}
          <ion-ripple-effect></ion-ripple-effect>
        </ion-button>
      </div>
    </form>
  </div>
</template>

<script>
import lodash from 'lodash'
import { computed, defineComponent, ref } from 'vue'
import { IonLabel, IonInput, IonCheckbox, IonButton, IonRippleEffect, IonImg } from '@ionic/vue'
import { useVuelidate } from '@vuelidate/core'
import { required, email } from '@vuelidate/validators'

import httpClient from '@/core/api/api.service'
import useAuth from '@/composables/auth'
import useFormControl from '@/composables/formControl'
import useFormWizard from '@/composables/formWizard'

import ErrorMessage from '@/components/Global/ErrorMessage'
import ServerValidation from '@/components/Global/ServerValidation'

export default defineComponent({
  name: 'RegistrationUserData',
  components: {
    IonLabel,
    IonInput,
    IonCheckbox,
    IonButton,
    IonRippleEffect,
    IonImg,
    ErrorMessage,
    ServerValidation,
  },
  setup() {
    const { silentLogin, AUTH_STATE } = useAuth()
    const { serverValidation, hasServerValidationError } = useFormControl()
    const { nextStep } = useFormWizard()

    const userName = ref('')
    const userEmail = ref('')
    const privacyPolicy = ref(false)
    const termsOfUse = ref(false)
    const isLoading = ref(false)

    const user = computed(() => AUTH_STATE.user)

    function setInitialState() {
      if (!lodash.isEmpty(user.value)) {
        userName.value = user.value.first_name || ''
        userEmail.value = user.value.email || ''
      }
    }

    setInitialState()

    const rules = {
      userName: { required },
      userEmail: { required, email },
      privacyPolicy: { privacy: val => val === true },
      termsOfUse: { acceptance: val => val === true },
    }

    const validations = useVuelidate(rules, {
      userName,
      userEmail,
      privacyPolicy,
      termsOfUse,
    })

    const submit = async () => {
      validations.value.$touch()

      if (validations.value.$invalid) return

      if (isLoading.value) return

      try {
        serverValidation.value = {}
        isLoading.value = true

        const payload = {
          first_name: userName.value,
          email: userEmail.value,
        }

        const { data } = await httpClient.save('users', payload)
        const user = data.data

        await silentLogin(user)

        nextStep()
        return Promise.resolve()
      } catch (error) {
        if (error.errStatus === 422 || error.errStatus === 404 || error.errStatus === 400) {
          serverValidation.value = Object.assign({}, error.errData.error)
        }

        return Promise.reject(error)
      } finally {
        isLoading.value = false
      }
    }

    return {
      submit,
      validations,
      isLoading,
      serverValidation,
      hasServerValidationError,
    }
  },
})
</script>

<style lang="scss" scoped>
.form-wizard-step-heading {
  ion-img {
    max-width: 75px;
  }
}

.form-group--checkbox {
  margin-bottom: 0;
}

ion-button {
  --background: #e6224a;
  --box-shadow: none;
}
</style>
